import { i18n } from '../../../i18n/index.js';
// Вложения: метаданные ходят через наш API, сам файл — напрямую в хранилище по
// короткоживущей presigned-ссылке. Через сервер тела файлов не проходят.
import { apiDelete, apiGet, apiPost } from '../../../shared/api-client/index.js';
import { entityListKey, type EntityListKey } from '../../../shared/api/entityQueryKeys.js';

// PR 5: `conversation` is the owner for files attached in the general /chat
// (no entity card): the file lives on the chat itself and is associated with
// the next user message instead of an entity record.
export type AttachmentEntity =
  | 'deal'
  | 'contact'
  | 'company'
  | 'task'
  | 'document'
  | 'meeting'
  | 'conversation';

export interface AttachmentItem {
  id: string;
  workspaceId: string;
  originalName: string;
  mimeType: string;
  size: number;
  status: 'pending' | 'uploaded';
  createdAt: string;
}

interface RequestUploadResponse {
  attachment: AttachmentItem;
  uploadUrl: string;
}

export function getAttachmentsQueryKey(
  workspaceId: string,
  entity: AttachmentEntity,
  entityId: string,
): EntityListKey {
  return entityListKey('attachments', workspaceId, { entity, entityId });
}

export async function listAttachments(
  entity: AttachmentEntity,
  entityId: string,
): Promise<AttachmentItem[]> {
  const search = new URLSearchParams({ entity, id: entityId });
  return apiGet<AttachmentItem[]>(`/attachments?${search.toString()}`);
}

export async function deleteAttachment(attachmentId: string): Promise<void> {
  await apiDelete(`/attachments/${attachmentId}`);
}

export async function getDownloadUrl(attachmentId: string): Promise<string> {
  const { url } = await apiGet<{ url: string }>(`/attachments/${attachmentId}/download`);
  return url;
}

/**
 * Полный цикл загрузки: ссылка → PUT в хранилище → подтверждение.
 * Без подтверждения запись остаётся `pending` и в списке не появляется, поэтому
 * оборванная загрузка не оставляет в карточке файл, которого нет.
 */
export async function uploadAttachment(
  entity: AttachmentEntity,
  entityId: string,
  file: File,
): Promise<AttachmentItem> {
  const { attachment, uploadUrl } = await apiPost<RequestUploadResponse, unknown>('/attachments', {
    entity,
    entityId,
    fileName: file.name,
    mimeType: file.type || 'application/octet-stream',
    size: file.size,
  });

  const uploaded = await fetch(uploadUrl, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
  }).catch((error: unknown) => {
    void deleteAttachment(attachment.id);
    throw error instanceof Error
      ? error
      : new Error(
          i18n.t(
            'crm.features.features.attachments.api.attachments.client.не_удалось_загрузить_файл',
          ),
        );
  });
  if (!uploaded.ok) {
    // Оборванная загрузка не должна оставлять в базе pending-запись: чистим сразу,
    // не дожидаясь часового обработчика чистки: пользователь видит результат сразу.
    void deleteAttachment(attachment.id);
    throw new Error(
      i18n.t('crm.features.dynamic.features.attachments.api.attachments.client.26', {
        p0: uploaded.status,
      }),
    );
  }

  return apiPost<AttachmentItem, Record<string, never>>(
    `/attachments/${attachment.id}/complete`,
    {},
  );
}
